import { Button } from "@/components/ui/button";
import React, { useEffect, useState } from "react";
import RequestLeaveDialog from "../request-leave-dialog/RequestLeaveDialog";
import { getColumns } from "./columns";
import { CalendarPlus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { useCurrentUser } from "@/context/UserContext";
import {
  fetchLeaveRequestsOfUser,
  setUserStatus,
  setUserType,
  resetUserFilters
} from "@/features/leaveRequests/leaveRequestSlice";
import UserDataTableWithToolbar from "./UserDataTableWithToolbar";
import { selectUserLeaveRequests, selectUserRequestsFilters } from "@/features/leaveRequests/leaveRequestSelector";

function LeaveTakenTable() {
  const { currentUser } = useCurrentUser();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const dispatch = useAppDispatch();
  const { requests, pagination } = useAppSelector(selectUserLeaveRequests);
  const { status, type } = useAppSelector(selectUserRequestsFilters);

  const [currentPage, setCurrentPage] = useState(1);
  const [typesSelected, setTypesSelected] = useState<string[]>(type);
  const [statusSelected, setStatusSelected] = useState<string[]>(status);

  const pageSize = 7; // or any size you want

  // Sync Redux state to local state when Redux changes
  useEffect(() => {
    setTypesSelected(type);
    setStatusSelected(status);
  }, [type, status]);

  // Fetch data when filters or pagination changes
  useEffect(() => {
    if (!currentUser) return;

    dispatch(fetchLeaveRequestsOfUser({
      userId: currentUser?.id,
      page: currentPage,
      status: statusSelected,
      type: typesSelected
    }));
  }, [dispatch, currentUser, currentPage, typesSelected, statusSelected]);

  // Update Redux when local state changes
  useEffect(() => {
    dispatch(setUserType(typesSelected));
  }, [dispatch, typesSelected]);

  useEffect(() => {
    dispatch(setUserStatus(statusSelected));
  }, [dispatch, statusSelected]);

  // Handle filter reset from this component
  const handleResetFilters = () => {
    dispatch(resetUserFilters());
    setTypesSelected([]);
    setStatusSelected([]);
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < pagination.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    if (pagination.totalPages > 0) {
      setCurrentPage(1);
    } else {
      setCurrentPage(0); // no pages available
    }
  }, [type, status, pagination.totalPages]);

  return (
    <div className="rounded-md w-full h-full p-5 bg-white outline outline-2 outline-gray-100 flex flex-col">
      {/* Sticky Header */}
      <div className="flex flex-col justify-between items-start mb-0 sticky top-0 bg-white z-1">
        <div className="flex flex-row gap-1 w-full justify-between" >
          <div className="font-bold text-2xl">Your Recent Requests</div>
          <Button onClick={() => setIsCreateOpen(true)}>
            <CalendarPlus />
            Request Leave
          </Button>
        </div>

        <div className="text-gray-500 text-sm">
          View your past leave requests or create a new one.
        </div>
      </div>

      {/* Scrollable DataTable */}
      <div className="overflow-auto flex-grow">
        <UserDataTableWithToolbar
          columns={getColumns()}
          data={requests}
          typesSelected={typesSelected}
          setTypesSelected={setTypesSelected}
          statusSelected={statusSelected}
          setStatusSelected={setStatusSelected}
          onResetFilters={handleResetFilters}
        />
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">
        <Button
          onClick={handlePrev}
          variant="outline"
          size="sm"
          disabled={currentPage <= 1}
        >
          Previous
        </Button>
        <span className="text-sm text-gray-600">
          Page {currentPage} of {pagination.totalPages || 1}
        </span>
        <Button
          onClick={handleNext}
          disabled={currentPage >= pagination.totalPages}
          variant="outline"
          size="sm"
        >
          Next
        </Button>
      </div>

      {/* Dialog */}
      <RequestLeaveDialog
        setIsOpen={setIsCreateOpen}
        isOpen={isCreateOpen}
      />
    </div>
  );
}

export default LeaveTakenTable;